'use client';

import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import { CheckIcon } from 'lucide-react';

import { cn } from '#/lib/utils';

function Dropdown({ ...props }: React.ComponentProps<typeof DropdownPrimitive.Root>) {
  return <DropdownPrimitive.Root data-slot="dropdown" {...props} />;
}

function DropdownTrigger({ ...props }: React.ComponentProps<typeof DropdownPrimitive.Trigger>) {
  return <DropdownPrimitive.Trigger data-slot="dropdown-trigger" {...props} />;
}

function DropdownPortal({ ...props }: React.ComponentProps<typeof DropdownPrimitive.Portal>) {
  return <DropdownPrimitive.Portal data-slot="dropdown-portal" {...props} />;
}

function DropdownContent({ className, ...props }: React.ComponentProps<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPortal data-slot="dropdown-portal">
      <DropdownPrimitive.Content
        data-slot="dropdown-content"
        align="start"
        side="bottom"
        sideOffset={5}
        collisionPadding={8}
        className={cn(
          'bg-popover data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 overflow-hidden rounded-lg border p-1 shadow-lg duration-200',
          className
        )}
        style={{
          width: 'var(--radix-dropdown-menu-trigger-width)',
          maxWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
        {...props}
      />
    </DropdownPortal>
  );
}

function DropdownItem({ className, ...props }: React.ComponentProps<typeof DropdownPrimitive.Item>) {
  return (
    <DropdownPrimitive.Item
      data-slot="dropdown-item"
      className={cn(
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-none select-none data-[disabled]:pointer-events-none',
        className
      )}
      {...props}
    />
  );
}

function DropdownCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.CheckboxItem>) {
  return (
    <DropdownPrimitive.CheckboxItem
      data-slot="dropdown-checkbox-item"
      className={cn(
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer items-center rounded-sm text-sm outline-none select-none data-[disabled]:pointer-events-none',
        className
      )}
      style={{
        padding: '0.375rem 0.5rem 0.375rem 2rem',
      }}
      {...props}
    >
      <DropdownPrimitive.ItemIndicator className="absolute left-2 flex h-4 w-4 items-center justify-center">
        <CheckIcon className="size-4" color="var(--primary)" />
      </DropdownPrimitive.ItemIndicator>

      {children}
    </DropdownPrimitive.CheckboxItem>
  );
}

function DropdownSeparator({ className, ...props }: React.ComponentProps<typeof DropdownPrimitive.Separator>) {
  return (
    <DropdownPrimitive.Separator
      data-slot="dropdown-separator"
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export {
  Dropdown,
  DropdownTrigger,
  DropdownPortal,
  DropdownContent,
  DropdownItem,
  DropdownCheckboxItem,
  DropdownSeparator,
};
